import { Grid, Tab, Tabs, Tooltip as MaterialTooltip } from "@mui/material";
import React, { useState, useEffect } from "react";
import { 
    Cell, 
    LabelList, 
    Legend, 
    Pie, 
    PieChart, 
    PolarAngleAxis, 
    PolarGrid, 
    PolarRadiusAxis, 
    Radar, 
    RadarChart, 
    ResponsiveContainer,
    Tooltip,
} from "recharts";
import { useTranslation } from "react-i18next";
import { analyzeMahjongSkill } from "../helper/mahjong";

import { getColors } from "../resources/colors";
import { GridCard } from "../StyledComponents";
import { GameLog, TurnActionType } from "../types/GameLog";
import MahjongGameAnalysisChartTabs from "./MahjongGameAnalysisChartTabs";
import _ from "lodash";

interface MahjongHistoryChartProps {
  mahjongData: GameLog[] | undefined,
  uid: string,
}

export default function MahjongHistoryChart(props: MahjongHistoryChartProps) {
  const [tableData, setTableData] = useState<Array<{ name: string, value: number }>>();

  const [mahjongSkillRadarData, setMahjongSkillRadarData] = useState<{ [key: string]: string | number }[]>();

  const [mahjongSkillDetailRadarData, setMahjongSkillDetailRadarData] = useState<{ [key: string]: string | number }[]>();

  const [mahjongGameAnalysisMode, setMahjongGameAnalysisMode] = useState<number>(0);

  const {t} = useTranslation();

  

  useEffect(() => {
    if (props.mahjongData !== undefined) {
      let tempArr = props.mahjongData.map(x => {
        if (x.winner !== -1) {
          return { winner: x.players[x.winner]?.user_id ?? "unknown", score: x.score };
        } else {
          return { winner: '', score: 0 };
        }
      })
      tempArr = tempArr.filter(x => x.winner !== '');
      if (tempArr !== undefined && tempArr.length > 0) {
        let _tableData: Array<{ name: string, value: number }> = [];
        tempArr.filter(x => x.winner === props.uid).forEach(x => {
          const tempFind = _tableData.find(y => y.name === x.score.toString());
          if (tempFind === undefined) {
            _tableData.push({ name: x.score.toString(), value: 1 });
          } else {
            const objIndex = _tableData.findIndex((obj => obj.name === tempFind.name));
            _tableData[objIndex].value = _tableData[objIndex].value + 1;
          }
        })
        const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
        _tableData = _tableData.map(x => { return x.name }).sort(collator.compare).map(x => { return { name: x, value: _tableData.filter(y => y.name === x)[0].value } });
        _tableData = _tableData.map(x => {
          return { name: x.name + t("mahjong_key.ban"), value: x.value };
        })
        _tableData.push({ name: t("mahjongHistoryChart.text_lose"), value: tempArr.filter(x => x.winner !== props.uid).length });
        setTableData(_tableData);
      }
    }
    
    const analyzeResult = analyzeMahjongSkill(props.mahjongData, props.uid);

    analyzeResult?.forEach(element => {
      element?.forEach(e => {
        e.skill = t(`mahjong.${e.skill}` as unknown as TemplateStringsArray) 
      })
    })
    
    setMahjongSkillRadarData(analyzeResult[0]);
    
    setMahjongSkillDetailRadarData(analyzeResult[1]);
  }, [])

  const mapScoreData = () => {
    const colors = getColors(tableData!.length);
    return tableData!.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.name === t("mahjongHistoryChart.text_lose") ? '#A9A9A9' : colors[index]} />);
  }

  return (
    <>
      <Grid item lg={4} md={12} sm={12} xs={12}>
        <GridCard title={t("mahjongHistoryChart.title_mahjong_stat")} desc={t("mahjongHistoryChart.desc_mahjong_stat")}>
          {tableData !== undefined ?
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Tooltip />
                <Legend />
                <Pie dataKey="value" data={tableData} outerRadius={100} animationBegin={400}>
                  <LabelList dataKey="name" position="outside" />
                  {
                    mapScoreData()
                  }
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            :
            (
              <div style={{ minHeight: 200 }}>
                <h3 style={{ marginTop: 100 }}>
                  {t("no__.data")}
                </h3>
                <p>
                  {t("common.start_a_game")}
                </p>
              </div>
            )}
        </GridCard>
      </Grid>
      <Grid item lg={8} md={12} sm={12} xs={12}>
        <GridCard title={t("mahjongHistoryChart.title_mahjong_ranking")} desc={t("mahjongHistoryChart.desc_mahjong_ranking")}>
          {!mahjongSkillRadarData && !mahjongSkillDetailRadarData && t("no__.data")}
            {(mahjongSkillRadarData && Object.values(mahjongSkillRadarData).every(skill => skill.data === 0)) ?
              (
                <div style={{ minHeight: 200 }}>
                  <h3 style={{ marginTop: 100 }}>
                    {t("no__.data")}
                  </h3>
                  <p>
                    {t("common.start_a_game")}
                  </p>
                </div>
              )
              :
              (
                <>
                  <Grid container>
                    {mahjongSkillRadarData &&
                      <Grid item md={6} xs={12}>
                        <ResponsiveContainer width="100%" height={300}>
                          <RadarChart outerRadius={60} width={250} height={250} data={mahjongSkillRadarData}>
                            <PolarGrid />
                            <PolarAngleAxis dataKey="skill" />
                            <PolarRadiusAxis angle={0} domain={[0, 100]} />
                            <defs>
                              <radialGradient id="radarchartColorToRed">
                                <stop offset="0%" stopColor="#FF0D0D" />
                                <stop offset="10%" stopColor="#FF4E11" />
                                <stop offset="30%" stopColor="#FF8E15" />
                                <stop offset="50%" stopColor="#FAB733" />
                                <stop offset="80%" stopColor="#ACB334" />
                                <stop offset="100%" stopColor="#69B34C" />
                              </radialGradient>
                            </defs>
                            <Radar name={t("mahjongHistoryChart.text_you")} dataKey="data" stroke="#555555" fill="url(#radarchartColorToRed)" fillOpacity={0.6} />
                            <Tooltip />
                          </RadarChart>
                        </ResponsiveContainer>
                      </Grid>
                    }
                    {
                      mahjongSkillDetailRadarData &&
                      <Grid item md={6} xs={12}>
                        <ResponsiveContainer width="100%" height={288}>
                          <RadarChart outerRadius={60} width={250} height={250} data={mahjongSkillDetailRadarData}>
                            <PolarGrid />
                            <PolarAngleAxis
                              dataKey="skill"
                            />
                            <PolarRadiusAxis angle={0} domain={[0, 100]} />
                            <defs>
                              <radialGradient id="radarchartColorToRed">
                                <stop offset="0%" stopColor="#FF0D0D" />
                                <stop offset="10%" stopColor="#FF4E11" />
                                <stop offset="30%" stopColor="#FF8E15" />
                                <stop offset="50%" stopColor="#FAB733" />
                                <stop offset="80%" stopColor="#ACB334" />
                                <stop offset="100%" stopColor="#69B34C" />
                              </radialGradient>
                            </defs>
                            <Radar name={t("mahjongHistoryChart.text_you")} dataKey="data" stroke="#555555" fill="url(#radarchartColorToRed)" fillOpacity={0.6} />
                            <Tooltip />
                          </RadarChart>
                        </ResponsiveContainer>
                      </Grid>
                    }
                  </Grid>
                </>
              )
            }
        </GridCard>
      </Grid>
      <Grid item md={12} xs={12}>
        <GridCard title={t("mahjongHistoryChart.title_mahjong_trend")} desc={t("mahjongHistoryChart.desc_mahjong_trend")}>
          {
            props.mahjongData && props.mahjongData.length !== 0 ?
            (
              <>
                <Tabs
                  value={mahjongGameAnalysisMode}
                  onChange={(e, v) => setMahjongGameAnalysisMode(v)}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab label={<MaterialTooltip title={t("mahjongHistoryChart.tab_title_1")} arrow placement="top"><span>{t("mahjongHistoryChart.integrated")}</span></MaterialTooltip>} value={0} style={{ width: 50 }} />
                  <Tab label={<MaterialTooltip title={t("mahjongHistoryChart.tab_title_2")} arrow placement="top"><span>{t("mahjong_key.eat")}</span></MaterialTooltip>} value={1} style={{ width: 50 }} />
                  <Tab label={<MaterialTooltip title={t("mahjongHistoryChart.tab_title_3")} arrow placement="top"><span>{t("mahjong_key.chunk")}</span></MaterialTooltip>} value={2} style={{ width: 50 }} />
                </Tabs>
                <MahjongGameAnalysisChartTabs data={props.mahjongData} uid={props.uid} mode={mahjongGameAnalysisMode as 0 | 1 | 2} />
              </>
            )
            :
            (
              <div style={{ minHeight: 200 }}>
                <h3 style={{ marginTop: 100 }}>
                  {t("no__.data")}
                </h3>
                <p style={{ }}>
                  {t("common.start_a_game")}
                </p>
              </div>
            )
          }
        </GridCard>
      </Grid>
    </>
  )
}